/**
 * Enrollments API – list, create, delete, update payment (Phase 4).
 * Parents: list by parentUserId (own), create. Tutors/Admin: list by classId, delete, update payment.
 */

import type {
  Enrollment,
  EnrollmentPayment,
  CreateEnrollmentRequest,
  UpdateEnrollmentPaymentRequest,
  RecordEnrollmentPaymentRequest,
  RecordEnrollmentPaymentResponse,
  SetEnrollmentFamilyDiscountRequest,
  SetEnrollmentFamilyDiscountResponse,
  SetEnrollmentTuitionDiscountRequest,
  SetEnrollmentTuitionDiscountResponse,
} from '@lambda/shared/types';
import { apiRequest } from './apiClient';

export interface ListEnrollmentsParams {
  parentUserId?: string;
  classId?: string;
  semesterId?: string;
}

export interface ListEnrollmentsResponse {
  enrollments: Enrollment[];
}

export const enrollmentsApi = {
  list(accessToken: string, params: ListEnrollmentsParams) {
    const searchParams = new URLSearchParams();
    if (params.parentUserId) searchParams.set('parentUserId', params.parentUserId);
    if (params.classId) searchParams.set('classId', params.classId);
    if (params.semesterId) searchParams.set('semesterId', params.semesterId);
    const qs = searchParams.toString();
    if (!qs) {
      return Promise.reject(new Error('Provide one of parentUserId, classId, or semesterId'));
    }
    return apiRequest<ListEnrollmentsResponse>(`/enrollments?${qs}`, accessToken, { method: 'GET' });
  },

  create(accessToken: string, body: CreateEnrollmentRequest) {
    return apiRequest<{ enrollment: Enrollment }>('/enrollments', accessToken, {
      method: 'POST',
      payload: body,
    });
  },

  delete(accessToken: string, enrollmentId: string) {
    return apiRequest<{ message: string }>(
      `/enrollments/${encodeURIComponent(enrollmentId)}`,
      accessToken,
      { method: 'DELETE' }
    );
  },

  recordPayment(
    accessToken: string,
    enrollmentId: string,
    body: RecordEnrollmentPaymentRequest
  ) {
    return apiRequest<RecordEnrollmentPaymentResponse>(
      `/enrollments/${encodeURIComponent(enrollmentId)}/payments`,
      accessToken,
      { method: 'POST', payload: body }
    );
  },

  deletePayment(accessToken: string, enrollmentId: string, paymentId: string) {
    return apiRequest<{
      enrollment: Enrollment;
      payments: EnrollmentPayment[];
      removedPaymentId: string;
    }>(
      `/enrollments/${encodeURIComponent(enrollmentId)}/payments/${encodeURIComponent(paymentId)}`,
      accessToken,
      { method: 'DELETE' }
    );
  },

  updatePayment(accessToken: string, enrollmentId: string, body: UpdateEnrollmentPaymentRequest) {
    return apiRequest<{ enrollment: Enrollment }>(
      `/enrollments/${encodeURIComponent(enrollmentId)}/payment`,
      accessToken,
      { method: 'PUT', payload: body }
    );
  },

  setFamilyDiscount(
    accessToken: string,
    enrollmentId: string,
    body: SetEnrollmentFamilyDiscountRequest
  ) {
    return apiRequest<SetEnrollmentFamilyDiscountResponse>(
      `/enrollments/${encodeURIComponent(enrollmentId)}/family-discount`,
      accessToken,
      { method: 'PATCH', payload: body }
    );
  },

  setTuitionDiscount(
    accessToken: string,
    enrollmentId: string,
    body: SetEnrollmentTuitionDiscountRequest
  ) {
    return apiRequest<SetEnrollmentTuitionDiscountResponse>(
      `/enrollments/${encodeURIComponent(enrollmentId)}/tuition-discount`,
      accessToken,
      { method: 'PATCH', payload: body }
    );
  },

  activateFromWaitlist(accessToken: string, enrollmentId: string) {
    return apiRequest<{ enrollment: Enrollment }>(
      `/enrollments/${encodeURIComponent(enrollmentId)}/activate`,
      accessToken,
      { method: 'PATCH' }
    );
  },
};
